import { useConsultation } from "../components/Consultation";
import { Reveal, Tag, LineReveal, SolidButton, ClipReveal } from "../components/Shared";
import { IMAGES } from "../data/content";

const STEPS = [
  { n: "01", title: "IMAGINE", body: "Your vision begins with a conversation. Occasion, mood, memory — we listen first, sketch second." },
  { n: "02", title: "CREATE", body: "Silhouette, fabric, colour and detail come together in muslin and mirror." },
  { n: "03", title: "REVEAL", body: "A garment created exclusively for you — fitted, finished, unforgettable." },
];

export default function BespokePage() {
  const { open } = useConsultation();
  return (
    <div className="bg-ink" data-testid="bespoke-page">
      <section className="relative flex min-h-[80vh] items-end overflow-hidden">
        <img src={IMAGES.bespoke} alt="The Rutu Bhonslé Bespoke label — signed with the house's signature orange" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="relative mx-auto w-full max-w-[1600px] px-5 pb-20 pt-40 sm:px-10">
          <Reveal><Tag>Bespoke</Tag></Reveal>
          <LineReveal
            lines={["MADE FOR YOU.", "NOT FOR EVERYONE."]}
            className="mt-6 font-serif text-6xl font-light leading-[0.98] tracking-tight sm:text-8xl"
            lineClassName="last:text-flame"
          />
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-lg text-lg font-light text-bone/80">
              From first sketch to final fitting, every bespoke creation begins with an idea — yours. Women, men and kids.
            </p>
          </Reveal>
          <Reveal delay={0.45} className="mt-10">
            <SolidButton onClick={open} testId="bespoke-page-book-button">Book a Bespoke Consultation</SolidButton>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 py-28 sm:px-10">
        <div className="grid gap-px bg-line/10 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12} className="bg-ink">
              <div className="p-12" data-testid={`bespoke-page-step-${s.n}`}>
                <span className="font-mono text-sm text-flame">{s.n}</span>
                <h2 className="mt-4 font-serif text-4xl font-light">{s.title}</h2>
                <p className="mt-4 max-w-xs text-sm font-light leading-relaxed text-fog">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-28 grid items-center gap-14 lg:grid-cols-2">
          <ClipReveal src={IMAGES.threads} alt="The atelier's wall of threads — every colour, every possibility" className="aspect-[4/3]" />
          <div>
            <Reveal><Tag>The Craft</Tag></Reveal>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight sm:text-5xl">The world's finest cloth,<br />cut around you.</h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-fog">
              We craft custom garments from prestigious bespoke fabrics — tailored to your measurements,
              your movement and your moment. Every commission is a limited edition of one.
            </p>
            <ul className="mt-8 space-y-3">
              {["Private consultations", "Hand-finished detailing", "Fittings at every stage", "Global shipping on all orders"].map((f) => (
                <li key={f} className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-bone/70">
                  <span className="h-px w-4 bg-flame" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
